<template>
  <div class="settings">
    <b-tabs>
      <b-tab-item :label="$t('settings.user.management')" :disabled="!isAdmin">
        <settings-users
          v-if="isAdmin"
          :users="users"
          :isAdmin="isAdmin"
          :loading="loadingUsers"
          @refetch-users="fetchUsers"
        ></settings-users>
      </b-tab-item>
      <b-tab-item :label="$t('settings.user.maintenance')" :disabled="!isAdmin">
        <settings-backup v-if="isAdmin"></settings-backup>
        <settings-logs v-if="isAdmin"></settings-logs>
      </b-tab-item>
      <b-tab-item :label="$t('settings.user.licensing')" :disabled="!isAdmin">
        <settings-license v-if="isAdmin"></settings-license>
      </b-tab-item>
      <b-tab-item :label="$t('settings.user.modules')" :disabled="!isAdmin">
        <settings-modules
          v-if="isAdmin"
          :modules="customModules"
          :loading="loadingCustomModules"
          @refetch="fetchCustomModules"
        ></settings-modules>
      </b-tab-item>
      <b-tab-item :label="$t('settings.user.misc')" :disabled="!isAdmin">
        <div v-if="isAdmin">
          <parametrs-list :items.sync="misc" :parameters="miscParameters"></parametrs-list>
          <edit-button v-if="!isMiscLoading" :editOptions="['save']" @click="saveMisc">
            <template>
              <span class="text">{{ $t('button.save') }}</span>
            </template>
          </edit-button>
          <loader v-else></loader>
        </div>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import SettingsUsers from './blocks/SettingsUsers'
import SettingsModules from './blocks/SettingsModules'
import SettingsLicense from './blocks/SettingsLicense'
import SettingsBackup from './blocks/SettingsBackup'
import SettingsLogs from './blocks/SettingsLogs'
import ParametrsList from '@/components/ParametrsList'
import Loader from '@/components/Loader'
import EditButton from '@/components/EditButton'

import { mapActions, createNamespacedHelpers } from 'vuex'

const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'SystemSettings',
  components: {
    SettingsUsers,
    SettingsModules,
    SettingsLicense,
    SettingsBackup,
    SettingsLogs,
    ParametrsList,
    Loader,
    EditButton
  },
  data() {
    return {
      currentUser: {},
      loadingCurrentUser: false,
      users: [],
      loadingUsers: false,
      customModules: [],
      loadingCustomModules: false,
      misc: {},
      miscParameters: [],
      isMiscLoading: false
    }
  },
  computed: {
    ...mapUserState({
      stateUser: state => {
        return state.user
      }
    }),
    isAdmin() {
      if (this.currentUser) {
        return this.currentUser.role === 'ADMIN'
      }
      return false
    }
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    ...mapActions('general', ['setUser', 'setUserAlarms']),
    saveMisc() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/misc`
      this.isMiscLoading = true
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          misc_settings: this.misc
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: `${this.$t('notification.updated', {
              msg: this.$t(`settings.user.misc`)
            })}.`,
            type: 'success'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.isMiscLoading = false
        })
    },
    fetchMisc() {
      if (!this.isMiscLoading) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/misc`
        this.isMiscLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.misc = data.config || {}
            this.miscParameters = data.properties.parameters
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.isMiscLoading = false
          })
      }
    },
    fetchCustomModules() {
      if (!this.loadingCustomModules) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/modules`
        this.loadingCustomModules = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.customModules = data
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingCustomModules = false
          })
      }
    },
    updateCurrentUser(item) {
      this.setUser(item)
      this.updateUser(item)
    },
    updateAlarams(item) {
      this.setUserAlarms(item)
      this.updateUser(this.stateUser)
    },
    updateUser(item) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/user`
      this.isUpdateUserLoading = true
      const name = item.name
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          user_config: item
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$emit('refetch-users')
          this.notifyUser({
            name: name,
            status: 'updated'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.isUpdateUserLoading = false
          this.isUpdateUser = false
        })
    },
    fetchCurrentUser() {
      if (!this.loadingCurrentUser) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/user`
        this.loadingCurrentUser = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.currentUser = data
            this.setUser(this.currentUser)
            this.setUserAlarms(this.currentUser.alarms)
            if (this.isAdmin) {
              this.fetchUsers()
              this.fetchCustomModules()
              this.fetchMisc()
            }
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingCurrentUser = false
          })
      }
    },
    fetchUsers() {
      if (!this.loadingUsers) {
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/settings/users`
        this.loadingUsers = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.users = data
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingUsers = false
          })
      }
    },
    notifyUser(variables) {
      this.$notify({
        message: `${this.$t('notification.userWas', {
          user: variables.name,
          status: this.$t(`notification.user.${variables.status}`)
        })}.`,
        type: 'success'
      })
    }
  }
}
</script>

<style lang="scss">
.is-main-column .page .page-slot.settings {
  padding: 0 !important;
  .settings {
    h2 {
      font-weight: bold;
      font-size: 150%;
      margin-bottom: 1rem;
    }
    .b-tabs {
      .tabs {
        border-bottom: 1px solid #dbdbdb;
        ul {
          padding-right: 2rem;
          padding-left: 2rem;
          li {
            font-weight: bold;
            &.is-active {
              background-color: #d3d3d3;
            }
            a {
              border-bottom: 1px solid #d3d3d3;
              color: unset;
              &:hover {
                background-color: #e9e9e9;
              }
            }
          }
        }
      }
      .tab-content {
        padding: 1rem 2rem;
      }
    }
    .card {
      margin-bottom: 1em;
      &:last-child {
        margin-bottom: 0;
      }
    }
    form {
      display: flow-root;
      width: 100%;
      .field {
        .field-label {
          min-width: 8rem;
          padding-top: 0;
          width: 100%;
          .label {
            text-align: right;
            width: 100%;
            min-width: 6.25rem;
          }
        }
        .control {
          font-size: 1.25em;
          .select {
            width: 100%;
            height: 1.5rem;
            select {
              width: 100%;
              height: 1.5rem;
              padding-top: 0;
              padding-bottom: 0;
              font-size: 0.9rem;
              padding-left: 0.25rem;
              padding-right: 0.25rem;
              &::after {
                right: 0.9rem;
              }
            }
            &:not(.is-multiple):not(.is-loading)::after {
              right: 0.9rem;
            }
          }
          &.datepicker,
          &.timepicker {
            .select {
              vertical-align: middle;
            }
            .icon {
              font-size: 0.9rem;
              height: 1.5rem;
            }
          }
          &.has-icons-right {
            .icon {
              font-size: 0.6875rem;
              right: 0.3125rem;
            }
          }
        }
        .upload.control {
          width: 100%;
          .upload-draggable {
            width: 100%;
            font-size: 0.9em;
          }
        }
        input {
          font-size: 0.9rem;
          height: 1.5rem;
        }
        textarea {
          font-size: 0.9rem;
        }
        .columns {
          margin-right: 0px;
          .column-button {
            padding-right: 0px;
            padding-left: 0px;
            .button {
              width: 100%;
              color: #b032e7;
              border-color: #b032e7;
              font-size: 0.9rem;
              height: 1.5rem;
            }
          }
        }
      }
    }
  }
}
</style>
